import { createId } from '../utils';
import { storageGet, storageRemove, storageSet } from './storage';
import type { UserContext } from '../types';

const ANON_KEY = 'trk_anonymous_id';
const SESSION_KEY = 'trk_session_id';
const SESSION_TS_KEY = 'trk_session_ts';
const USER_KEY = 'trk_user_id';
const TRAITS_KEY = 'trk_user_traits';

/** 30 分钟无活动则新开会话 */
const SESSION_TTL_MS = 30 * 60 * 1000;

function readTraits(): Record<string, unknown> {
  const raw = storageGet(TRAITS_KEY);
  if (!raw) return {};
  try {
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function writeTraits(traits: Record<string, unknown>): void {
  storageSet(TRAITS_KEY, JSON.stringify(traits));
}

function ensureAnonymousId(): string {
  let id = storageGet(ANON_KEY);
  if (!id) {
    id = createId('anonymous');
    storageSet(ANON_KEY, id);
  }
  return id;
}

function ensureSessionId(): string {
  const now = Date.now();
  const last = Number(storageGet(SESSION_TS_KEY) || 0);
  let sessionId = storageGet(SESSION_KEY);
  if (!sessionId || !last || now - last > SESSION_TTL_MS) {
    sessionId = createId('session');
    storageSet(SESSION_KEY, sessionId);
  }
  storageSet(SESSION_TS_KEY, String(now));
  return sessionId;
}

export function touchSession(): string {
  return ensureSessionId();
}

export function getUserContext(): UserContext {
  const rawUserId = storageGet(USER_KEY);
  const userId = rawUserId && rawUserId.trim() ? rawUserId : null;
  const traits = readTraits();
  return {
    user_id: userId,
    anonymous_id: ensureAnonymousId(),
    session_id: ensureSessionId(),
    login_status: userId ? 'logged_in' : 'anonymous',
    ...traits,
  };
}

export function identifyUser(userId: string, traits?: Record<string, unknown>): void {
  storageSet(USER_KEY, userId);
  if (traits && Object.keys(traits).length) {
    writeTraits({ ...readTraits(), ...traits });
  }
  touchSession();
}

export function resetUserIdentity(): void {
  storageRemove(USER_KEY);
  // 清除登录态，保留 anonymous_id；开启新 session
  writeTraits({});
  storageSet(SESSION_KEY, createId('session'));
  storageSet(SESSION_TS_KEY, String(Date.now()));
}

export function setUserTraits(traits: Record<string, unknown>): void {
  writeTraits({ ...readTraits(), ...traits });
}
